import { useImageGallery } from "../contexts/ImageGalleryContext";
import React, { useState, useCallback, useEffect, useRef, useMemo, Suspense } from "react";
import type { Token, TokenSnapshot, SidebarTabId } from "../types";
import type { LocationLink, AiPersona, AiFocus } from "@roll1/shared";
import { useSessionContext } from "../contexts/SessionContext";
import { useCanvasContext } from "../contexts/CanvasContext";
import { useToolContext } from "../contexts/ToolContext";
import { useTokenContext } from "../contexts/TokenContext";
import { useChatContext } from "../contexts/ChatContext";
import { useInitiativeContext } from "../contexts/InitiativeContext";
import { INITIATIVE_OPEN_EVENT } from "../lib/initiative-requests";
import { useSheetContext } from "../contexts/SheetContext";
import { useWorkspaceContext } from "../contexts/WorkspaceContext";
import { useLayoutContext } from "../contexts/LayoutContext";
import { ActorProvider } from "../contexts/ActorContext";
import { useMultiplayerSync } from "../hooks/useMultiplayerSync";
import { useDrawingSelection } from "../hooks/useDrawingSelection";
import { useAppShortcuts } from "../hooks/useAppShortcuts";
import { useToolOptions } from "../hooks/useToolOptions";
import FloatingToolbar from "./FloatingToolbar";
import CanvasArea from "./CanvasArea";
import { MapViewStatus } from "./MapViewStatus";
import Sidebar, { SIDEBAR_COLLAPSED_WIDTH } from "./Sidebar";
import SidebarTabs, { getTabLabel } from "./SidebarTabs";
const AppDialogs = React.lazy(() => import("./AppDialogs"));
const CharacterHudOverlay = React.lazy(() => import("./character-hud/CharacterHudOverlay").then((m) => ({ default: m.CharacterHudOverlay })));
import type { NavigateRequest } from "./NavigateRequestToast";
import { useReleaseNotes } from "../hooks/useReleaseNotes";
const FullscreenMiniPanel = React.lazy(() => import("./FullscreenMiniPanel"));
import PopoutWindow from "./PopoutWindow";
import { TableSearch } from "./search/TableSearch";
import type { MagneticEdge } from "./PopoutWindow";
import type { PopoutEntry } from "../hooks/usePopout";
import { useWindowManager } from "../hooks/useWindowManager";
import type { SnapState } from "../hooks/useWindowManager";
const SnapZoneOverlay = React.lazy(() => import("./SnapZoneOverlay"));
const MagneticEdgeIndicator = React.lazy(() => import("./MagneticEdgeIndicator"));
import LoadingSkeleton from "./LoadingSkeleton";
const ReconnectOverlay = React.lazy(() => import("./ReconnectOverlay"));
import { useAiChat } from "../hooks/useAiChat";
import { useVoiceSession } from "../hooks/useVoiceSession";
const SpeakerMappingModal = React.lazy(() =>
  import("./SpeakerMappingModal").then((m) => ({ default: m.SpeakerMappingModal })),
);
import { usePlayerAi } from "../hooks/usePlayerAi";
import { usePersonaMemory } from "../hooks/usePersonaMemory";
import { useBatchProgress } from "../hooks/useBatchProgress";
import { useVisibilityReport } from "../hooks/useVisibilityReport";
const BatchProgressIndicator = React.lazy(() => import("./BatchProgressIndicator"));
import { useInpainting } from "../hooks/useInpainting";
import { useOutpainting } from "../hooks/useOutpainting";
import type { OutpaintDirection } from "../hooks/useOutpainting";
import { useDetailPass } from "../hooks/useDetailPass";
const OutpaintDialog = React.lazy(() => import("./OutpaintDialog"));
import { toast } from "sonner";
import { useI18n } from "../i18n";
import type { TranslationKeys } from "../i18n";
import { useUndoContext } from "../contexts/UndoContext";
const AlertOverlay = React.lazy(() => import("./AlertOverlay"));
const DetailMapDialog = React.lazy(() => import("./DetailMapDialog"));
import SessionBar from "./SessionBar";
const DetachedPanelContent = React.lazy(() => import("./DetachedPanelContent"));
import { useBugReport } from "../hooks/useBugReport";
import { useJournalContext } from "../contexts/JournalContext";
import { useJournalPopout } from "../hooks/useJournalPopout";
const JournalEntryEditor = React.lazy(() => import("./JournalEntryEditor"));
import "../styles/alerts.css";
import { useIsMobile } from "../hooks/useIsMobile";
const MobileLayout = React.lazy(() => import("./mobile/MobileLayout"));
const OfflineBanner = React.lazy(() => import("./OfflineBanner"));
const PWAInstallPrompt = React.lazy(() => import("./PWAInstallPrompt"));
import "../styles/mobile.css";
import { useAoeTemplates } from "../hooks/useAoeTemplates";
import { useSnapToGrid } from "../hooks/useSnapToGrid";
import { useAoePlacement } from "../hooks/useAoePlacement";
import { AoeTemplatesViewProvider } from "../contexts/AoeTemplatesViewContext";
import { useCanvasInteraction } from "../contexts/CanvasInteractionContext";
const MapObjectsManager = React.lazy(() => import("./domain/MapObjectsManager"));
const TextToolManager = React.lazy(() => import("./domain/TextToolManager"));
const LocationLinkManager = React.lazy(() => import("./domain/LocationLinkManager"));
const QuickAccessPanel = React.lazy(() => import("./QuickAccessPanel/QuickAccessPanel"));
const ActorChatSync = React.lazy(() => import("./ActorChatSync"));
import { DrawingSelectionProvider } from "../contexts/DrawingSelectionContext";
const DrawingManager = React.lazy(() => import("./domain/DrawingManager"));
const FogManager = React.lazy(() => import("./domain/FogManager"));
import PageManager from "./domain/PageManager";
const NoteManager = React.lazy(() => import("./domain/NoteManager"));
const TokenManager = React.lazy(() => import("./domain/TokenManager"));
const DragDropManager = React.lazy(() => import("./domain/DragDropManager"));
const WallToolManager = React.lazy(() => import("./domain/WallToolManager"));
const PinManager = React.lazy(() => import("./domain/PinManager"));
const DialogOrchestrator = React.lazy(() => import("./DialogOrchestrator"));
const SidebarContent = React.lazy(() => import("./SidebarContent"));
import { Loader2 } from "lucide-react";
import LazyErrorBoundary from "./LazyErrorBoundary";
import { onOpenRequest } from "../lib/search/openRequest";
import { useLayoutTabSync } from "../hooks/useLayoutTabSync";

const InitiativePanel = React.lazy(() => import("./InitiativePanel"));
const InitiativeHeaderActions = React.lazy(() => import("./InitiativePanel").then((m) => ({ default: m.InitiativeHeaderActions })));
const DicePanel = React.lazy(() => import("./DicePanel"));
const ChatPanel = React.lazy(() => import("./ChatPanel"));
const AiChatPanel = React.lazy(() => import("./AiChatPanel"));
const SettingsPanel = React.lazy(() => import("./SettingsPanel"));
const ImportWizard = React.lazy(() =>
  import("./ImportWizard").then((m) => ({ default: m.ImportWizard })),
);

export default function AppShell() {
  const isMobile = useIsMobile();
  const { t, locale } = useI18n();
  const releaseNotes = useReleaseNotes();
  const journal = useJournalContext();
  const journalPopout = useJournalPopout();
  const layoutCtx = useLayoutContext();
  const { get } = useCanvasInteraction();
  const {
    mp,
    pages,
    isGM,
    outgoingPageId,
    sessionSettings,
    setSessionSettings,
  } = useSessionContext();
  const {
    engine,
    drawing,
    fog,
    setSnapToGrid,
    drawColor,
    setDrawColor,
    drawWidth,
    setDrawWidth,
  } = useCanvasContext();
  const { state: toolState, setTool: setCurrentTool } = useToolContext();
  const currentTool = toolState.active;
  const toolOptions = useToolOptions();
  const snapHook = useSnapToGrid();

  // Track AI persona from session state (052-ai-persona)
  const [aiPersona, setAiPersona] = useState<AiPersona | null | undefined>(
    () => mp.serverState?.aiContext?.persona,
  );

  // D-20 (owner 28.10: «карточка мастеру»): a fired map trigger whose action would show the players something asks the
  // GM with a button. It belongs to no AI conversation (conversationId «trigger:<id>»), so the AI chat drops it — the
  // GM gets it here, as a notice that stays until answered; the button runs the kept action (ai:run-confirmed).
  useEffect(() => {
    if (!mp.session || !isGM) return;
    return mp.onMessage((msg) => {
      if (msg.type !== "ai:needs-confirmation") return;
      const ask = msg as unknown as { conversationId?: string; actionId: string; target?: { name?: string } };
      if (!ask.conversationId?.startsWith("trigger:")) return;
      toast(t("trigger.askShow").replace("{what}", ask.target?.name ?? ""), {
        id: ask.actionId,
        duration: Infinity,
        action: { label: t("trigger.show"), onClick: () => mp.send({ type: "ai:run-confirmed", actionId: ask.actionId } as never) },
        cancel: { label: t("trigger.notNow"), onClick: () => undefined },
      });
    });
  }, [mp, isGM, t]);

  // Sync persona from ai-context:updated WS messages
  useEffect(() => {
    if (!mp.session) return;
    return mp.onMessage((msg) => {
      if (msg.type === "ai-context:updated") {
        const payload = msg as unknown as {
          aiContext?: { persona?: AiPersona | null };
        };
        setAiPersona(payload.aiContext?.persona);
      }
    });
  }, [mp]);

  // Initialize persona from serverState when joining
  useEffect(() => {
    if (mp.serverState?.aiContext) {
      setAiPersona(mp.serverState.aiContext.persona);
    }
  }, [mp.serverState]);

  // Sync snap (with Alt-override) → canvas refs (BI-001)
  useEffect(() => {
    setSnapToGrid(snapHook.snapActive);
  }, [snapHook.snapActive, setSnapToGrid]);
  useEffect(() => {
    setDrawColor(toolOptions.options.drawColor);
  }, [toolOptions.options.drawColor, setDrawColor]);
  useEffect(() => {
    setDrawWidth(toolOptions.options.drawWidth);
  }, [toolOptions.options.drawWidth, setDrawWidth]);
  useEffect(() => {
    fog.fogMode.current = toolOptions.options.fogMode as any;
  }, [toolOptions.options.fogMode, fog]);
  useEffect(() => {
    drawing.shapeType.current = toolOptions.options.shapeType;
  }, [toolOptions.options.shapeType, drawing]);
  useEffect(() => {
    fog.setBrushSize(toolOptions.options.fogBrushSize);
  }, [toolOptions.options.fogBrushSize, fog]);

  const { tokens, notes: notesHook } = useTokenContext();
  const tokensRef = useRef(tokens);
  const notesRef = useRef(notesHook);
  useEffect(() => {
    tokensRef.current = tokens;
    notesRef.current = notesHook;
  });
  const undoStack = useUndoContext();
  const drawingSelection = useDrawingSelection(
    engine,
    drawing,
    () => notesHook.notes,
  );
  const clearAllDrawingsRef = useRef<() => void>(() => {});
  const pageOpsRef = useRef<
    import("../contexts/PageOpsContext").PageOpsContextValue | null
  >(null);
  const aoeSend = useCallback(
    (msg: Record<string, unknown>) => {
      if (mp.session) mp.send(msg as any);
    },
    [mp],
  );
  const aoeTemplates = useAoeTemplates(
    aoeSend,
    outgoingPageId,
    engine.markDirty,
  );
  const aoePlacement = useAoePlacement(aoeTemplates);
  const { chat, rolls } = useChatContext();
  const { initiative } = useInitiativeContext();
  const {
    sheets: charSheets,
    templates: tmpl,
    popout,
    onSendSheet,
  } = useSheetContext();
  const workspace = useWorkspaceContext();
  const {
    sidebarConfig,
    setSidebarConfig,
    toggleCollapsed,
    isFullscreen,
    exitFullscreen,
    detachedPanels,
  } = workspace;
  const setActiveTab = useCallback((tab: SidebarTabId) => {
    if (tab === "journal") setJournalNotification(false);
    setSidebarConfig((prev) => ({ ...prev, activeTab: tab }));
  }, [setSidebarConfig]);
  // The remembered tab belongs to the browser, not to the role: a player never lands on a GM-only tab by old memory.
  const rememberedTab = sidebarConfig.activeTab;
  const sidebarTabs = {
    activeTab: !isGM && (rememberedTab === "plan" || rememberedTab === "alerts") ? ("map" as SidebarTabId) : rememberedTab,
    setActiveTab,
  };

  // LayoutContext.sidebarTab → the remembered tab (QAP tab pinning); only real changes — see useLayoutTabSync (error #185).
  useLayoutTabSync(layoutCtx.state.sidebarTab, sidebarConfig.activeTab, setActiveTab);

  // AI Chat hooks (state preserved across tab switches)
  // AT-4: where the GM is — sent with every request (the current beat, the GM's map, the players' map, the selected tokens).
  const aiFocusRef = useRef<() => AiFocus | undefined>(() => undefined);
  // «Замечания» ask Tia through the seam (aiBridge): the GM's chat opens its window when a request comes.
  const openAiWindowRef = useRef<() => void>(() => undefined);
  const aiChat = useAiChat({ getFocus: () => aiFocusRef.current(), ...(isGM ? { bridge: { open: () => openAiWindowRef.current() } } : {}) });
  aiFocusRef.current = (): AiFocus | undefined => (isGM ? {
    place: 'table',
    beatId: mp.serverState?.currentBeatId ?? null,
    myPageId: pages.viewingPageId ?? null,
    playersPageId: pages.activePageId ?? null,
    tokenIds: [...tokens.selectedTokenIds],
  } : undefined);
  const playerAi = usePlayerAi();
  const personaMemory = usePersonaMemory(mp);

  // 049 Phase 1: batch progress indicator (WS state:batch-progress → UI toast)
  const batchProgress = useBatchProgress();

  // 049 Phase 3: player visibility report (players only — informs AI what player sees)
  const getVisibleEntities = useCallback(() => {
    if (isGM) return null;
    const state = mp.serverState;
    if (!state) return null;
    const page = state.pages.find((p) => p.id === outgoingPageId);
    if (!page) return null;
    return {
      visibleTokenIds: page.tokens.map((t) => t.id),
      visibleNoteIds: (page.notes ?? []).map((n) => n.id),
      visibleAnnotationIds: (page.annotations ?? []).map((a) => a.id),
      currentPageId: page.id,
    };
  }, [isGM, mp.serverState, outgoingPageId]);
  const visibilityReport = useVisibilityReport(getVisibleEntities);

  useEffect(() => {
    if (!mp.session) return;
    return mp.onMessage((msg) => {
      batchProgress.handleMessage(msg);
    });
  }, [mp, batchProgress.handleMessage]);

  useEffect(() => {
    if (isGM) return;
    visibilityReport.scheduleReport();
  }, [isGM, outgoingPageId, pages.pages, visibilityReport]);

  // Discord voice session (Spec 055) — subscribes to voice:status-updated broadcasts
  const voice = useVoiceSession(mp);
  const [speakerMappingOpen, setSpeakerMappingOpen] = useState(false);

  // Group Q review item 2: region edits run on their own; the result waits for «Принять» in the compare window.
  const imageGallery = useImageGallery();
  // The live page store, not mp.serverState: that is the join snapshot — a map accepted or a page made since is not in
  // it (owner 28.10: «Было» was empty in the compare window). No fallback to the snapshot: the store is seeded with
  // every page of it on join (usePages hydrate), so a fallback could only return a stale copy.
  const livePage = pages.getPageData(outgoingPageId);
  const pageMapUrl = livePage?.mapUrl ?? null;

  // AI Inpainting hook (Spec 055 Phase 2)
  const inpaintingHook = useInpainting({
    registerEdit: imageGallery?.registerEdit,
    mapUrl: pageMapUrl,
    sessionId: mp.session?.id ?? "",
    participantId: mp.participant?.id ?? "",
    pageId: outgoingPageId,
    mapWidth: engine.mapWidth,
    mapHeight: engine.mapHeight,
    mapScale: engine.mapScaleRef.current,
    mapOffset: engine.mapOffsetRef.current,
    send: mp.send,
  });

  // AI Outpainting hook (Spec 055 Phase 4)
  const outpaintingHook = useOutpainting({
    registerEdit: imageGallery?.registerEdit,
    mapUrl: pageMapUrl,
    onStarted: () => setOutpaintDialogOpen(false),
    sessionId: mp.session?.id ?? "",
    participantId: mp.participant?.id ?? "",
    pageId: outgoingPageId,
    mapWidth: engine.mapWidth,
    mapHeight: engine.mapHeight,
    send: mp.send,
  });

  // Outpaint dialog opener callback (used by edge buttons + toolbar)
  const handleOpenOutpaint = useCallback(
    (dir?: OutpaintDirection) => {
      if (dir) outpaintingHook.setDirection(dir);
      setOutpaintDialogOpen(true);
    },
    [outpaintingHook],
  );

  // Current page data for detail pass
  const currentPageData = {
    mapUrl: pageMapUrl,
    locationLinks: livePage?.locationLinks ?? ([] as import("@roll1/shared").LocationLink[]),
    name: livePage?.name ?? "",
  };

  // AI Detail Pass hook (Spec 055 Phase 3)
  const detailPassHook = useDetailPass({
    registerEdit: imageGallery?.registerEdit,
    sessionId: mp.session?.id ?? "",
    participantId: mp.participant?.id ?? "",
    pageId: outgoingPageId,
    mapUrl: currentPageData.mapUrl,
    mapScale: engine.mapScaleRef.current,
    mapOffset: engine.mapOffsetRef.current,
    locationLinks: currentPageData.locationLinks,
    send: mp.send,
    onServerMessage: mp.onMessage,
    // The accepted detail opens for the GM only: the players are moved by «Show to players…» with its confirmation, never by this.
    navigateToPage: (pageId: string) => {
      pageOpsRef.current?.pageCallbacks.preview(pageId);
    },
  });

  // Sync inpainting tool state with ToolContext
  useEffect(() => {
    if (currentTool === "inpaint-rect") {
      inpaintingHook.setTool("rect");
    } else if (currentTool === "inpaint-lasso") {
      inpaintingHook.setTool("lasso");
    } else if (
      inpaintingHook.tool !== null &&
      !inpaintingHook.region &&
      !inpaintingHook.generating &&
      !inpaintingHook.previewUrl
    ) {
      inpaintingHook.setTool(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTool]);

  // Revert to select tool when ai-edit hooks are cancelled/reset
  useEffect(() => {
    if (
      (currentTool === "inpaint-rect" || currentTool === "inpaint-lasso") &&
      inpaintingHook.tool === null &&
      !inpaintingHook.generating &&
      !inpaintingHook.previewUrl
    ) {
      setCurrentTool("select");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inpaintingHook.tool,
    inpaintingHook.generating,
    inpaintingHook.previewUrl,
  ]);

  useEffect(() => {
    if (
      currentTool === "detail-rect" &&
      !detailPassHook.dialogOpen &&
      !detailPassHook.generating
    ) {
      setCurrentTool("select");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailPassHook.dialogOpen, detailPassHook.generating]);

  const [showImportWizard, setShowImportWizard] = useState(false);
  const [outpaintDialogOpen, setOutpaintDialogOpen] = useState(false);

  // Bug report: Ctrl+Alt+B (Cmd+Alt+B on macOS) → click on element → dialog
  const bugReport = useBugReport();

  // Journal notification badge
  const [journalNotification, setJournalNotification] = useState(false);

  // Dialog states
  const [directSheetId, setDirectSheetId] = useState<string | null>(null);

  /** Open a sheet in the HUD (FullSheetOverlay auto-opens on directSheetId).
   * The old version also programmatically SELECTED the sheet's token — a hack
   * to bind the selection-driven HUD, which left a phantom floating HUD after
   * the overlay closed. The HUD is actor-driven now (ActorContext), so an
   * explicit open is just directSheetId. */
  const openSheetInHud = useCallback((sheetId: string) => {
    setDirectSheetId(sheetId);
  }, []);

  // «Где используется» of the gallery at the table: the sheet in the HUD, a map as the GM's own preview (players are
  // never moved by it).
  useEffect(() => onOpenRequest((target) => {
    if (target.kind === "sheet") { openSheetInHud(target.id); return true; }
    if (target.kind === "page" && isGM && pageOpsRef.current) { pageOpsRef.current.pageCallbacks.preview(target.id); return true; }
    return false;
  }), [openSheetInHud, isGM]);

  const clearDirectSheetId = useCallback(() => {
    setDirectSheetId(null);
  }, []);

  const [linkContextMenu, setLinkContextMenu] = useState<{
    link: LocationLink;
    x: number;
    y: number;
  } | null>(null);
  const [aiMapOpen, setAiMapOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // Ctrl+K (group M): the app-wide search, the same key and look as in Подготовка; by the physical key (a Russian layout gives «л»).
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.ctrlKey || event.metaKey) && event.code === "KeyK") {
        event.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  const [initiativeFloating, setInitiativeFloating] = useState(false);
  const [diceFloating, setDiceFloating] = useState(false);
  const [aiFloating, setAiFloating] = useState(false);
  openAiWindowRef.current = () => setAiFloating(true);
  const [settingsFloating, setSettingsFloating] = useState(false);
  // Floating windows anchored on the right open left of the docked sidebar, so
  // the default chat never covers its panels (the game plan's actions included).
  const wm = useWindowManager({
    rightInset: isMobile || isFullscreen
      ? 0
      : layoutCtx.state.sidebarOpen ? layoutCtx.state.sidebarWidth : SIDEBAR_COLLAPSED_WIDTH,
  });
  const wmRef = useRef(wm);
  wmRef.current = wm;

  // Snap zone preview and magnetic edge state
  const [snapPreview, setSnapPreview] = useState<SnapState | null>(null);
  const [magneticEdges, setMagneticEdges] = useState<MagneticEdge[]>([]);

  // Track window IDs for each floating panel type
  const initiativeWinId = useRef<string | null>(null);
  const diceWinId = useRef<string | null>(null);
  const chatWinId = useRef<string | null>(null);
  const aiWinId = useRef<string | null>(null);
  const settingsWinId = useRef<string | null>(null);
  const detachedWinIds = useRef<Record<string, string>>({});

  // The GM's window is remembered on the server (late joins / reloads). A player's window is his own: it opens by itself
  // when a fight starts and shuts when it ends (initiative brief 25.10, Р11); his toggle and × are local.
  const toggleInitiativePanel = useCallback(() => {
    setInitiativeFloating((prev) => {
      const next = !prev;
      if (mp.session && isGM)
        mp.send({ type: "initiative:panel", visible: next } as any);
      return next;
    });
  }, [mp, isGM]);

  // The window's × must clear the SERVER panelVisible too — a local-only
  // close left it true, so the "closed" panel resurrected for late joiners
  // and on every reload.
  const closeInitiativePanel = useCallback(() => {
    setInitiativeFloating(false);
    if (mp.session && isGM)
      mp.send({ type: "initiative:panel", visible: false } as any);
  }, [mp, isGM]);

  // The token menu's «В инициативу» / «Хиты…» open the tracker window (initiative brief 25.10, Р3/Р10).
  useEffect(() => {
    const open = (): void => {
      setInitiativeFloating((prev) => {
        if (!prev && mp.session && isGM) mp.send({ type: "initiative:panel", visible: true } as any);
        return true;
      });
    };
    window.addEventListener(INITIATIVE_OPEN_EVENT, open);
    return () => window.removeEventListener(INITIATIVE_OPEN_EVENT, open);
  }, [mp, isGM]);

  // Open/close wm windows when floating state changes
  useEffect(() => {
    if (initiativeFloating && !initiativeWinId.current) {
      initiativeWinId.current = wmRef.current.open("initiative");
    } else if (!initiativeFloating && initiativeWinId.current) {
      wmRef.current.close(initiativeWinId.current);
      initiativeWinId.current = null;
    }
  }, [initiativeFloating]);

  useEffect(() => {
    if (diceFloating && !diceWinId.current) {
      diceWinId.current = wmRef.current.open("dice");
    } else if (!diceFloating && diceWinId.current) {
      wmRef.current.close(diceWinId.current);
      diceWinId.current = null;
    }
  }, [diceFloating]);

  useEffect(() => {
    if (mp.session && !chatWinId.current) {
      chatWinId.current = wmRef.current.open("chat");
    }
  }, [mp.session]);

  useEffect(() => {
    if (aiFloating && !aiWinId.current) {
      aiWinId.current = wmRef.current.open("ai-chat");
    } else if (!aiFloating && aiWinId.current) {
      wmRef.current.close(aiWinId.current);
      aiWinId.current = null;
    }
  }, [aiFloating]);

  useEffect(() => {
    if (settingsFloating && !settingsWinId.current) {
      settingsWinId.current = wmRef.current.open("settings");
    } else if (!settingsFloating && settingsWinId.current) {
      wmRef.current.close(settingsWinId.current);
      settingsWinId.current = null;
    }
  }, [settingsFloating]);

  const getDetachedEntry = useCallback(
    (tabId: string): PopoutEntry => {
      if (!detachedWinIds.current[tabId]) {
        detachedWinIds.current[tabId] = wmRef.current.open("detached", tabId);
      }
      const entry = wmRef.current.getEntry(detachedWinIds.current[tabId]);
      if (entry) return entry;
      return {
        id: `panel-${tabId}`,
        sheetId: tabId,
        x: 100,
        y: 100,
        width: 400,
        height: 500,
        zIndex: 2000 /* matches --z-floating-window */,
      };
    },
    [wm],
  );

  const handleDetachedMove = useCallback(
    (tabId: string, x: number, y: number) => {
      const winId = detachedWinIds.current[tabId];
      if (winId) wm.move(winId, x, y);
    },
    [wm],
  );

  const handleDetachedResize = useCallback(
    (tabId: string, width: number, height: number) => {
      const winId = detachedWinIds.current[tabId];
      if (winId) wm.resize(winId, width, height);
    },
    [wm],
  );

  const handleDetachedBringToFront = useCallback(
    (tabId: string) => {
      const winId = detachedWinIds.current[tabId];
      if (winId) wm.bringToFront(winId);
    },
    [wm],
  );
  // Tool options now from ToolContext via useToolOptions (T005)
  const linkDrawMode = toolOptions.options.linkDrawMode;
  const shapeType = toolOptions.options.shapeType;
  const fogBrushSize = toolOptions.options.fogBrushSize;
  const fogMode = toolOptions.options.fogMode;
  const reFogMode = toolOptions.options.reFogMode;

  const [avatarSize, setAvatarSize] = useState<
    import("./CharacterPortraits").AvatarSize
  >(() => {
    try {
      const stored = localStorage.getItem("avatar-size");
      if (stored && ["small", "medium", "large"].includes(stored))
        return stored as import("./CharacterPortraits").AvatarSize;
    } catch {}
    return "medium";
  });
  const [navigateRequests, setNavigateRequests] = useState<NavigateRequest[]>(
    [],
  );

  // Auto-close journal editor popouts when entry is deleted
  useEffect(() => {
    for (const pe of journalPopout.popouts) {
      if (!journal.entries.find((e) => e.id === pe.sheetId)) {
        journalPopout.closeEditor(pe.sheetId);
      }
    }
  }, [journal.entries, journalPopout]);

  // WS sync
  const { saveCurrentPageToStore, loadPageToHooks } = useMultiplayerSync({
    onJournalRevealed: () => {
      if (sidebarTabs.activeTab !== "journal") {
        setJournalNotification(true);
      }
    },
    onInitiativePanelToggle: useCallback((visible: boolean) => {
      setInitiativeFloating(visible);
    }, []),
    onPing: useCallback(
      (x: number, y: number, by: string, color?: string) => {
        get("ping:add")?.(x, y, by, color);
      },
      [get],
    ),
  });

  // Handle incoming template:* WS messages (AoE)
  useEffect(() => {
    if (!mp.session) return;
    return mp.onMessage((msg: any) => {
      if (msg.type === "template:placed" || msg.type === "template:removed") {
        aoeTemplates.handleTemplateMessage(msg);
      }
    });
  }, [mp, aoeTemplates]);

  // H-4: Delete → Ctrl+Z brings the token back for everyone. The undo entry of a deleted token re-sends it
  // through these refs (own refs, like DragDropManager's: the shortcut does not depend on TokenManager).
  const sendCreateRef = useRef<(token: TokenSnapshot) => void>(() => {});
  const sendDeleteRef = useRef<(tokenId: string) => void>(() => {});
  sendCreateRef.current = (token: TokenSnapshot) => {
    if (mp.session) mp.send({ type: "token:create", token, pageId: outgoingPageId });
  };
  sendDeleteRef.current = (tokenId: string) => {
    if (mp.session) mp.send({ type: "token:delete", tokenId, pageId: outgoingPageId });
  };

  // Keyboard shortcuts (registered in ShortcutContext)
  useAppShortcuts({
    setSnapToGrid,
    setShortcutsOpen,
    engine,
    tokens,
    initiative,
    mp,
    isGM,
    outgoingPageId,
    sidebarTabs,
    isFullscreen,
    exitFullscreen,
    undoStack,
    toggleSidebarCollapsed: toggleCollapsed,
    fog,
    currentTool,
    setFogBrushSize: (size: number) =>
      toolOptions.setOption("fogBrushSize", size),
    drawToolOptions: {
      drawWidth: toolOptions.options.drawWidth,
      setOption: (_key: "drawWidth", value: number) =>
        toolOptions.setOption("drawWidth", value),
    },
    textHook: {
      selectedTextId: get("text:selected-id")?.() ?? null,
      deleteText: (id: string) => get("text:delete-selected")?.(id),
      setSelectedTextId: () => {},
    },
    handlePasteFromClipboard: () => get("token:paste")?.(),
    handleDeleteText: (textId: string) => get("text:delete-selected")?.(textId),
    sendCreateRef,
    sendDeleteRef,
    toggleQuickAccess: () =>
      layoutCtx.dispatch({ type: "layout:toggleQuickAccess" }),
    toggleInitiative: toggleInitiativePanel,
    // Q also drives ruler waypoints (measure tool / token drag); while a ruler
    // is live it owns Q, so the quick-access toggle stays suppressed.
    isRulerActive: () =>
      drawing.tokenDragPoints.current.length > 0 ||
      drawing.measurePoints.current.length > 0,
  });

  // Clear undo/redo stack on page switch (US5)
  const viewingPageId = pages.viewingPageId;
  useEffect(() => {
    undoStack.clear();
  }, [viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear undo/redo stack on reconnect (US5)
  const mpStatus = mp.status;
  const prevMpStatus = useRef(mpStatus);
  useEffect(() => {
    // Detect reconnection: status changed from non-connected back to connected
    if (mpStatus === "connected" && prevMpStatus.current !== "connected") {
      undoStack.clear();
    }
    prevMpStatus.current = mpStatus;
  }, [mpStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // T068: Edge-hover exit from fullscreen (right edge, last 20px)
  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e: MouseEvent) => {
      if (window.innerWidth - e.clientX <= 20) {
        exitFullscreen();
      }
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, [isFullscreen, exitFullscreen]);

  // === Stabilized callbacks for CanvasArea (React.memo) ===
  const getTokenDescription = useCallback(
    (t: import("../types").Token) => {
      if (t.characterSheetId) {
        const sheet = charSheets.sheets.find(
          (s) => s.id === t.characterSheetId,
        );
        if (sheet) return sheet.tokenDescription;
      }
      return t.description;
    },
    [charSheets.sheets],
  );

  const onAvatarSizeChange = useCallback(
    (s: import("./CharacterPortraits").AvatarSize) => {
      setAvatarSize(s);
      try {
        localStorage.setItem("avatar-size", s);
      } catch {}
    },
    [],
  );

  const onContextMenuLink = useCallback(
    (link: import("@roll1/shared").LocationLink, x: number, y: number) => {
      setLinkContextMenu({ link, x, y });
    },
    [],
  );

  const aoeAffectedTokenIds = useMemo(() => {
    const ids = new Map<string, string>();
    for (const [
      templateId,
      arr,
    ] of aoeTemplates.affectedTokens.entries()) {
      const template = aoeTemplates.templates.get(templateId);
      const color = template?.color ?? "rgba(255, 68, 68, 0.3)";
      for (const id of arr) ids.set(id, color);
    }
    return ids;
  }, [aoeTemplates.affectedTokens, aoeTemplates.templates]);

  // === Stabilized callbacks for SidebarTabs (React.memo) ===
  const onExpandWithTab = useCallback(
    (tab: import("../types").SidebarTabId) => {
      setSidebarConfig((prev) => ({
        ...prev,
        collapsed: false,
        activeTab: tab,
      }));
    },
    [],
  );

  const onSettingsOpen = useCallback(() => {
    setSettingsFloating((prev) => !prev);
  }, []);

  const notificationTabs = useMemo(
    () => (journalNotification ? (["journal"] as import("../types").SidebarTabId[]) : []),
    [journalNotification],
  );

  // T107: Show skeleton while session data is still loading from server
  if (!mp.serverState) {
    return <LoadingSkeleton />;
  }

  // Token upload trigger — hidden <input> lives in TokenManager, we find it by selector
  const handleTokenUpload = useCallback(() => {
    const input = document.querySelector<HTMLInputElement>(
      'input[type="file"][accept="image/*"][hidden]',
    );
    input?.click();
  }, []);

  // --- Mobile layout (024-mobile-adaptation) ---
  if (isMobile) {
    return (
      <ActorProvider>
      <DrawingSelectionProvider
        drawingSelection={drawingSelection}
        clearAllDrawingsRef={clearAllDrawingsRef}
      >
        <DrawingManager />
        <FogManager />
        <NoteManager />
        <WallToolManager pageId={outgoingPageId} />
        <TokenManager
          onOpenSheet={openSheetInHud}
        />
        <DragDropManager />
        <PinManager onOpenJournalEntry={isGM ? (entryId: string) => journalPopout.openEditor(entryId) : undefined} />
        <PageManager
          saveCurrentPageToStore={saveCurrentPageToStore}
          loadPageToHooks={loadPageToHooks}
          handleTokenUpload={handleTokenUpload}
          pageOpsRef={pageOpsRef}
        >
          <TableSearch
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          openSheet={openSheetInHud}
          openJournalEditor={(entryId: string) => journalPopout.openEditor(entryId)}
          setActiveTab={setActiveTab}
        />
          <div id="app" data-component="AppShell" className="mobile-mode">
            <LazyErrorBoundary><Suspense fallback={<div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
              <AoeTemplatesViewProvider value={aoeTemplates.templates}>
              <MobileLayout
                aiChat={isGM ? aiChat : playerAi}
                onBugReport={() => bugReport.openDialog()}
                isDialogOpen={bugReport.dialogOpen}
                onOpenSheet={openSheetInHud}
                onEditJournalEntry={
                  isGM
                    ? (entryId: string) => journalPopout.openEditor(entryId)
                    : undefined
                }
              />
              </AoeTemplatesViewProvider>
            </Suspense></LazyErrorBoundary>
            {/* GJ-8: the phone had no mount of the sheet at all — a touch on one's own character did nothing. */}
            <Suspense fallback={null}>
              <CharacterHudOverlay sheetOnly directSheetId={directSheetId} onClearDirectSheetId={clearDirectSheetId} />
            </Suspense>
            {/* Shared dialogs (same as desktop) */}
            <Suspense fallback={null}>
              <AppDialogs
                linkContextMenu={linkContextMenu}
                setLinkContextMenu={setLinkContextMenu}
                aiMapOpen={aiMapOpen}
                setAiMapOpen={setAiMapOpen}
                engine={engine}
                drawing={drawing}
                mp={mp}
                outgoingPageId={outgoingPageId}
                isGM={isGM}
                onEditLink={(link: LocationLink) =>
                  get("link:open-edit-dialog")?.(link)
                }
                navigateRequests={navigateRequests}
                setNavigateRequests={setNavigateRequests}
              />
              <AlertOverlay />
              <DialogOrchestrator
                shortcutsOpen={shortcutsOpen}
                setShortcutsOpen={setShortcutsOpen}
                releaseNotes={releaseNotes}
                bugReport={bugReport}
              />
            </Suspense>
          </div>
        </PageManager>
      </DrawingSelectionProvider>
      </ActorProvider>
    );
  }

  const session = mp.session!;
  return (
    <ActorProvider>
    <DrawingSelectionProvider
      drawingSelection={drawingSelection}
      clearAllDrawingsRef={clearAllDrawingsRef}
    >
      <Suspense fallback={null}>
        <DrawingManager />
        <FogManager />
        <NoteManager />
        <WallToolManager pageId={outgoingPageId} />
        <TokenManager onOpenSheet={openSheetInHud} />
        <DragDropManager />
        <PinManager onOpenJournalEntry={isGM ? (entryId: string) => journalPopout.openEditor(entryId) : undefined} />
      </Suspense>
      <PageManager
        saveCurrentPageToStore={saveCurrentPageToStore}
        loadPageToHooks={loadPageToHooks}
        handleTokenUpload={handleTokenUpload}
        pageOpsRef={pageOpsRef}
      >
        <TableSearch
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          openSheet={openSheetInHud}
          openJournalEditor={(entryId: string) => journalPopout.openEditor(entryId)}
          setActiveTab={setActiveTab}
        />
        <div
          id="app"
          data-component="AppShell"
          className={isFullscreen ? "fullscreen-mode" : ""}
        >
          {/* PWA: offline banner + install prompt */}
          <Suspense fallback={null}>
            <OfflineBanner />
            <PWAInstallPrompt />
          </Suspense>
          {/* T109: Show reconnect overlay when WebSocket disconnects */}
          {mp.status !== "connected" && <Suspense fallback={null}><ReconnectOverlay /></Suspense>}
          {batchProgress.isActive && (
            <Suspense fallback={null}>
              <BatchProgressIndicator progress={batchProgress.progress} />
            </Suspense>
          )}

          {!isFullscreen && (
            <SessionBar
              sessionCode={session.code}
              participantName={mp.participant?.name}
              participantRole={mp.participant?.role}
              onlineCount={mp.participants.filter((p) => p.online).length}
              totalCount={mp.participants.length}
              connectionStatus={mp.status}
              isGM={isGM}
              onToggleFullscreen={workspace.toggleFullscreen}
              currentVersion={releaseNotes.currentVersion}
              hasUpdate={releaseNotes.hasUpdate}
              onShowWhatsNew={() => releaseNotes.setShowWhatsNew(true)}
              onDisconnect={mp.disconnect}
              gameSystem={mp.serverState?.gameSystem ?? 'dnd5e'}
              participants={mp.participants}
              currentParticipantId={mp.participant?.id}
              voice={voice}
              onOpenSpeakerMapping={() => setSpeakerMappingOpen(true)}
              onOpenSearch={() => setSearchOpen(true)}
            />
          )}

          {/* Spec 055: Speaker mapping modal (GM manually maps Discord users → participants) */}
          {speakerMappingOpen && (
            <Suspense fallback={null}>
              <SpeakerMappingModal
                open={speakerMappingOpen}
                onClose={() => setSpeakerMappingOpen(false)}
                voice={voice}
                participants={mp.participants}
                characterSheets={charSheets.sheets}
              />
            </Suspense>
          )}

          {!isFullscreen && (
            <>
              <FloatingToolbar
                isGM={isGM}
                onInitiativeToggle={toggleInitiativePanel}
                initiativeOpen={initiativeFloating}
                onDiceToggle={() => setDiceFloating((prev) => !prev)}
                diceOpen={diceFloating}
                onAiToggle={() => setAiFloating((prev) => !prev)}
                aiOpen={aiFloating}
                toolOptionsProps={{
                  onFogModeChange: (mode) => {
                    fog.cancelPolygon();
                  },
                  onToggleReFogMode: () => {
                    const next = fog.toggleReFogMode();
                    toolOptions.setOption("reFogMode", next);
                  },
                  onFogCoverAll: () => get("fog:cover-all")?.(),
                  onFogClearAll: () => get("fog:clear-all")?.(),
                  measurementMode: sessionSettings.measurementMode,
                  gridSizeFt: sessionSettings.gridSizeFt,
                  onMeasurementModeChange: (mode) => {
                    setSessionSettings((prev) => ({
                      ...prev,
                      measurementMode: mode,
                    }));
                    if (mp.session)
                      mp.send({
                        type: "settings:update",
                        settings: { measurementMode: mode },
                      });
                  },
                  onGridSizeFtChange: (ft) => {
                    setSessionSettings((prev) => ({ ...prev, gridSizeFt: ft }));
                    if (mp.session)
                      mp.send({
                        type: "settings:update",
                        settings: { gridSizeFt: ft },
                      });
                  },
                  navigationMode: pages.navigationMode,
                  onNavigationModeChange: (mode) => {
                    if (mp.session)
                      mp.send({
                        type: "location:set-navigation-mode",
                        mode,
                      } as any);
                  },
                  onAoeStartPlacement: aoePlacement.startPlacement,
                  onAoeClearAll: aoeTemplates.removeAllTemplates,
                  fogSystemMode: fog.fogModeRef.current,
                  onFogSystemModeChange: (mode) => get("fog:set-mode")?.(mode),
                  onOpenSettings: isGM
                    ? (_tab?: string) => {
                        setSettingsFloating(true);
                      }
                    : undefined,
                  onExpandMap:
                    isGM && currentPageData.mapUrl
                      ? () => handleOpenOutpaint()
                      : undefined,
                  onSketchToMap:
                    mp.session && isGM ? () => setAiMapOpen(true) : undefined,
                }}
              />
            </>
          )}

          <div
            id="main-content"
            className={isFullscreen ? "fullscreen-canvas" : ""}
          >
            {/* The GM's own view (preview) is a sharp, working map: what it is
                and what the players see is told by the status strip above it
                (any sidebar section, fullscreen), not by blurring the canvas
                (GM Studio 5.1). */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MapViewStatus
                viewingPageId={pages.viewingPageId}
                onReturnToPlayers={() => {
                  saveCurrentPageToStore();
                  pages.returnToActive();
                }}
                // Sidebar polish П9: the path of the map is in the strip (no bar of its own, no «← Назад»);
                // a level opens that map for this GM alone, as the old breadcrumb did.
                onOpenLevel={(pageId) => {
                  if (mp.session) {
                    saveCurrentPageToStore();
                    pages.setViewingPageId(pageId);
                    const pd = pages.getPageData(pageId);
                    if (pd) loadPageToHooks(pd);
                  }
                }}
              />
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  display: "flex",
                  position: "relative",
                }}
              >
                <CanvasArea
                  drawingSelection={drawingSelection}
                  aoeTemplates={aoeTemplates.templates}
                  aoeAffectedTokenIds={aoeAffectedTokenIds}
                  aoePlacement={aoePlacement}
                  linkDrawMode={linkDrawMode}
                  onContextMenuLink={isGM ? onContextMenuLink : undefined}
                  getTokenDescription={getTokenDescription}
                  avatarSize={avatarSize}
                  onAvatarSizeChange={onAvatarSizeChange}
                  inpainting={isGM ? inpaintingHook : null}
                  detailPass={isGM ? detailPassHook : null}
                  outpainting={isGM ? outpaintingHook : null}
                  onOpenOutpaint={isGM ? handleOpenOutpaint : undefined}
                  onOpenSheet={openSheetInHud}
                  directSheetId={directSheetId}
                  onClearDirectSheetId={clearDirectSheetId}
                />
                <Suspense fallback={null}><TextToolManager /></Suspense>
              </div>
            </div>

            {/* Detail Map Dialog (Spec 055 Phase 3) */}
            {isGM && (
              <Suspense fallback={null}>
                <DetailMapDialog
                  detailPass={detailPassHook}
                  parentPageName={currentPageData.name}
                />
              </Suspense>
            )}

            {/* Outpaint Dialog (Spec 055 Phase 4) */}
            {isGM && (
              <Suspense fallback={null}>
                <OutpaintDialog
                  open={outpaintDialogOpen}
                  onOpenChange={setOutpaintDialogOpen}
                  outpainting={outpaintingHook}
                  mapWidth={engine.mapWidth}
                  mapHeight={engine.mapHeight}
                />
              </Suspense>
            )}

            {!isFullscreen && (
              <Sidebar>
                <div className="sidebar-layout">
                  <div className="sidebar-left-column">
                    <div className="sidebar-header">
                      <button
                        className="sidebar-collapse-btn"
                        onClick={toggleCollapsed}
                        title={
                          sidebarConfig.collapsed
                            ? `${t("session.expandSidebar")} (Ctrl+B)`
                            : `${t("session.collapseSidebar")} (Ctrl+B)`
                        }
                        aria-label={
                          sidebarConfig.collapsed
                            ? t("session.expandSidebar")
                            : t("session.collapseSidebar")
                        }
                      >
                        {sidebarConfig.collapsed ? "\u00BB" : "\u00AB"}
                      </button>
                    </div>
                    <SidebarTabs
                      activeTab={sidebarTabs.activeTab}
                      onTabChange={sidebarTabs.setActiveTab}
                      collapsed={sidebarConfig.collapsed}
                      isGM={isGM}
                      hasSession={!!mp.session}
                      notificationTabs={notificationTabs}
                      onExpandWithTab={onExpandWithTab}
                      onDetach={detachedPanels.detach}
                      isDetached={detachedPanels.isDetached}
                      onSettingsOpen={isGM ? onSettingsOpen : undefined}
                    />
                  </div>
                  {!sidebarConfig.collapsed && (
                    <div className="sidebar-content-panel">
                      {detachedPanels.isDetached(sidebarTabs.activeTab) ? (
                        <div
                          className="sidebar-detached-placeholder"
                          data-testid="detached-placeholder"
                        >
                          {t("session.panelDetached")}
                        </div>
                      ) : (
                        <Suspense fallback={<div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
                        <SidebarContent
                          tab={sidebarTabs.activeTab}
                          isGM={isGM}
                          onOpenSheet={(id) => openSheetInHud(id)}
                          onEditJournalEntry={
                            isGM
                              ? (entryId) => journalPopout.openEditor(entryId)
                              : undefined
                          }
                        />
                        </Suspense>
                      )}
                    </div>
                  )}
                </div>
              </Sidebar>
            )}

            {/* Detached sidebar panels as PopoutWindows (T092/T094/T095) */}
            {Array.from(detachedPanels.detachedPanels).map((tabId) => (
              <PopoutWindow
                key={tabId}
                entry={getDetachedEntry(tabId)}
                title={t(getTabLabel(tabId) as keyof TranslationKeys)}
                onClose={() => {
                  const winId = detachedWinIds.current[tabId];
                  if (winId) {
                    wm.close(winId);
                    delete detachedWinIds.current[tabId];
                  }
                  detachedPanels.dock(tabId);
                }}
                onBringToFront={() => handleDetachedBringToFront(tabId)}
                onMove={(x, y) => handleDetachedMove(tabId, x, y)}
                onResize={(w, h) => handleDetachedResize(tabId, w, h)}
                onSnap={(s) => {
                  const winId = detachedWinIds.current[tabId];
                  if (winId) wm.snap(winId, s);
                }}
                onUnsnap={() => {
                  const winId = detachedWinIds.current[tabId];
                  if (winId) wm.unsnap(winId);
                }}
                snapState={
                  detachedWinIds.current[tabId]
                    ? wm.findWindow("detached", tabId)?.snapState
                    : undefined
                }
                allWindows={wm.windows}
                onSnapPreview={setSnapPreview}
                onMagneticEdges={setMagneticEdges}
              >
                <Suspense fallback={<div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
                  <DetachedPanelContent
                    tabId={tabId}
                    isGM={isGM}
                    onOpenSheet={openSheetInHud}
                    onEditJournalEntry={
                      isGM
                        ? (entryId: string) => journalPopout.openEditor(entryId)
                        : undefined
                    }
                  />
                </Suspense>
              </PopoutWindow>
            ))}

            {/* Floating initiative panel */}
            {initiativeFloating &&
              (isGM || initiative.entries.length > 0) &&
              initiativeWinId.current &&
              wm.getEntry(initiativeWinId.current) && (
                <PopoutWindow
                  entry={wm.getEntry(initiativeWinId.current)!}
                  title={t("initiative.title")}
                  windowKey="initiative"
                  autoHeightMax={560}
                  flushBody
                  closeTitle={t("initiative.closeHint")}
                  headerActions={<Suspense fallback={null}><InitiativeHeaderActions /></Suspense>}
                  onClose={closeInitiativePanel}
                  onBringToFront={() =>
                    wm.bringToFront(initiativeWinId.current!)
                  }
                  onMove={(x, y) => wm.move(initiativeWinId.current!, x, y)}
                  onResize={(w, h) => wm.resize(initiativeWinId.current!, w, h)}
                  onSnap={(s) => wm.snap(initiativeWinId.current!, s)}
                  onUnsnap={() => wm.unsnap(initiativeWinId.current!)}
                  snapState={wm.findWindow("initiative")?.snapState}
                  allWindows={wm.windows}
                  onSnapPreview={setSnapPreview}
                  onMagneticEdges={setMagneticEdges}
                >
                  <LazyErrorBoundary><Suspense fallback={<div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
                    <InitiativePanel floating onOpenSheet={openSheetInHud} />
                  </Suspense></LazyErrorBoundary>
                </PopoutWindow>
              )}

            {/* Floating dice panel */}
            {diceFloating &&
              diceWinId.current &&
              wm.getEntry(diceWinId.current) && (
                <PopoutWindow
                  entry={wm.getEntry(diceWinId.current)!}
                  title={t("dice.title")}
                  onClose={() => setDiceFloating(false)}
                  onBringToFront={() => wm.bringToFront(diceWinId.current!)}
                  onMove={(x, y) => wm.move(diceWinId.current!, x, y)}
                  onResize={(w, h) => wm.resize(diceWinId.current!, w, h)}
                  onSnap={(s) => wm.snap(diceWinId.current!, s)}
                  onUnsnap={() => wm.unsnap(diceWinId.current!)}
                  snapState={wm.findWindow("dice")?.snapState}
                  allWindows={wm.windows}
                  onSnapPreview={setSnapPreview}
                  onMagneticEdges={setMagneticEdges}
                >
                  <LazyErrorBoundary><Suspense fallback={<div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
                    <DicePanel floating />
                  </Suspense></LazyErrorBoundary>
                </PopoutWindow>
              )}

            {/* Floating chat — always visible */}
            {mp.session &&
              chatWinId.current &&
              wm.getEntry(chatWinId.current) && (
                <PopoutWindow
                  entry={wm.getEntry(chatWinId.current)!}
                  title={t("chat.title")}
                  onClose={() => {}}
                  hideClose
                  onBringToFront={() => wm.bringToFront(chatWinId.current!)}
                  onMove={(x, y) => wm.move(chatWinId.current!, x, y)}
                  onResize={(w, h) => wm.resize(chatWinId.current!, w, h)}
                  onSnap={(s) => wm.snap(chatWinId.current!, s)}
                  onUnsnap={() => wm.unsnap(chatWinId.current!)}
                  snapState={wm.findWindow("chat")?.snapState}
                  allWindows={wm.windows}
                  onSnapPreview={setSnapPreview}
                  onMagneticEdges={setMagneticEdges}
                >
                  <LazyErrorBoundary><Suspense fallback={<div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
                    <ChatPanel floating />
                  </Suspense></LazyErrorBoundary>
                </PopoutWindow>
              )}

            {/* Floating AI chat — GM and Player */}
            {aiFloating && aiWinId.current && wm.getEntry(aiWinId.current) && (
              <PopoutWindow
                entry={wm.getEntry(aiWinId.current)!}
                title={aiPersona === undefined || aiPersona === null || (aiPersona.type === "builtin" && aiPersona.sourceId === "tia") ? t("ai.title") : aiPersona.displayName}
                onClose={() => setAiFloating(false)}
                onBringToFront={() => wm.bringToFront(aiWinId.current!)}
                onMove={(x, y) => wm.move(aiWinId.current!, x, y)}
                onResize={(w, h) => wm.resize(aiWinId.current!, w, h)}
                onSnap={(s) => wm.snap(aiWinId.current!, s)}
                onUnsnap={() => wm.unsnap(aiWinId.current!)}
                snapState={wm.findWindow("ai-chat")?.snapState}
                allWindows={wm.windows}
                onSnapPreview={setSnapPreview}
                onMagneticEdges={setMagneticEdges}
              >
                <LazyErrorBoundary><Suspense fallback={<div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
                  {isGM ? (
                    <AiChatPanel
                      aiChat={aiChat}
                      isGM
                      floating
                      sessionId={mp.session?.id}
                      persona={aiPersona}
                      personaMemory={personaMemory}
                      onSaveChronicle={(text) => {
                        mp.send({
                          type: "journal:create",
                          entryType: "handout",
                          name: "Chronicle",
                          data: { content: text },
                        } as any);
                      }}
                      canOpenChange={(ch) => ch.entity.kind === "sheet" || ch.entity.kind === "journal" || ch.entity.kind === "objective"}
                      onOpenChange={(ch) => {
                        if (ch.entity.kind === "sheet") openSheetInHud(ch.entity.id);
                        else if (ch.entity.kind === "journal") journalPopout.openEditor(ch.entity.id);
                        else if (ch.entity.kind === "objective" && ch.entity.parentId !== undefined) journalPopout.openEditor(ch.entity.parentId);
                      }}
                    />
                  ) : (
                    <AiChatPanel
                      aiChat={playerAi}
                      isGM={false}
                      floating
                      persona={aiPersona}
                      canOpenChange={ch => ch.entity.kind === 'sheet'}
                      onOpenChange={ch => { if (ch.entity.kind === 'sheet') openSheetInHud(ch.entity.id); }}
                    />
                  )}
                </Suspense></LazyErrorBoundary>
              </PopoutWindow>
            )}

            {/* Floating settings panel (GM only) */}
            {settingsFloating &&
              settingsWinId.current &&
              wm.getEntry(settingsWinId.current) && (
                <PopoutWindow
                  entry={wm.getEntry(settingsWinId.current)!}
                  title={t("settings.title")}
                  onClose={() => setSettingsFloating(false)}
                  onBringToFront={() => wm.bringToFront(settingsWinId.current!)}
                  onMove={(x, y) => wm.move(settingsWinId.current!, x, y)}
                  onResize={(w, h) => wm.resize(settingsWinId.current!, w, h)}
                  onSnap={(s) => wm.snap(settingsWinId.current!, s)}
                  onUnsnap={() => wm.unsnap(settingsWinId.current!)}
                  snapState={wm.findWindow("settings")?.snapState}
                  allWindows={wm.windows}
                  onSnapPreview={setSnapPreview}
                  onMagneticEdges={setMagneticEdges}
                >
                  <LazyErrorBoundary><Suspense fallback={<div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
                    <SettingsPanel onImport={() => setShowImportWizard(true)} />
                  </Suspense></LazyErrorBoundary>
                </PopoutWindow>
              )}

            {/* Campaign Import Wizard (GM only) */}
            {isGM && showImportWizard && mp.session?.id && (
              <LazyErrorBoundary>
                <Suspense fallback={<div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
                  <ImportWizard
                    sessionId={mp.session.id}
                    onClose={() => setShowImportWizard(false)}
                  />
                </Suspense>
              </LazyErrorBoundary>
            )}

            {/* Journal editor popout windows */}
            {journalPopout.popouts.map((pe) => {
              const entry = journal.entries.find((e) => e.id === pe.sheetId);
              if (!entry) return null;
              return (
                <PopoutWindow
                  key={pe.id}
                  entry={pe}
                  title={entry.name}
                  onClose={() => journalPopout.closeEditor(pe.sheetId)}
                  onBringToFront={() => journalPopout.bringToFront(pe.id)}
                  onMove={(x, y) => journalPopout.updatePosition(pe.id, x, y)}
                  onResize={(w, h) => journalPopout.updateSize(pe.id, w, h)}
                >
                  <Suspense fallback={<div className="flex items-center justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
                    <JournalEntryEditor
                      entry={entry}
                      entries={journal.entries}
                      pages={pages.pages}
                      notes={notesRef.current.notes}
                      sheets={charSheets.sheets}
                      isGM={isGM}
                      onUpdate={(changes) => {
                        if (mp.session)
                          mp.send({
                            type: "journal:update",
                            entryId: entry.id,
                            changes,
                          } as any);
                      }}
                      onReveal={() => {
                        if (mp.session)
                          mp.send({
                            type: "journal:reveal",
                            entryId: entry.id,
                          } as any);
                      }}
                      onPin={(pinned) => {
                        if (mp.session)
                          mp.send({
                            type: "journal:pin",
                            entryId: entry.id,
                            pinned,
                          } as any);
                      }}
                      onLink={(targetId, relation) => {
                        if (mp.session)
                          mp.send({
                            type: "journal:link",
                            sourceId: entry.id,
                            targetId,
                            relation,
                          } as any);
                      }}
                      onUnlink={(targetId, relation) => {
                        if (mp.session)
                          mp.send({
                            type: "journal:unlink",
                            sourceId: entry.id,
                            targetId,
                            relation,
                          } as any);
                      }}
                      onNavigateToEntry={(entryId) =>
                        journalPopout.openEditor(entryId)
                      }
                      // C-1: «Go to map» opens the map for the GM alone; showing it to the players is the strip's own confirmed action.
                      onNavigateToPage={(id) => {
                        pageOpsRef.current?.pageCallbacks.preview(id);
                        // The editor window covers the map the GM came to see: it closes (its edits are already sent).
                        journalPopout.closeEditor(pe.sheetId);
                      }}
                      onOpenSheet={(id) => openSheetInHud(id)}
                    />
                  </Suspense>
                </PopoutWindow>
              );
            })}
          </div>

          {isFullscreen && <Suspense fallback={null}><FullscreenMiniPanel /></Suspense>}

          <Suspense fallback={null}>
            <DialogOrchestrator
              shortcutsOpen={shortcutsOpen}
              setShortcutsOpen={setShortcutsOpen}
              releaseNotes={releaseNotes}
              bugReport={bugReport}
            />
            <AppDialogs
              linkContextMenu={linkContextMenu}
              setLinkContextMenu={setLinkContextMenu}
              aiMapOpen={aiMapOpen}
              setAiMapOpen={setAiMapOpen}
              engine={engine}
              drawing={drawing}
              mp={mp}
              outgoingPageId={outgoingPageId}
              isGM={isGM}
              onEditLink={(link: LocationLink) =>
                get("link:open-edit-dialog")?.(link)
              }
              navigateRequests={navigateRequests}
              setNavigateRequests={setNavigateRequests}
            />
            <LocationLinkManager />
            <ActorChatSync />
            <MapObjectsManager />
            {layoutCtx.state.quickAccessOpen && <QuickAccessPanel />}
            <AlertOverlay />
          </Suspense>
          <Suspense fallback={null}>
            <SnapZoneOverlay
              activeZone={snapPreview}
              visible={snapPreview !== null}
            />
            <MagneticEdgeIndicator
              edges={magneticEdges}
              visible={magneticEdges.length > 0}
            />
          </Suspense>
        </div>
      </PageManager>
    </DrawingSelectionProvider>
    </ActorProvider>
  );
}
